import { useCallback, useEffect, useState } from "react";
import { History, PackageCheck, Truck } from "lucide-react";
import { commandesService, expeditionsService } from "@/api/services";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { construireCreationExpedition, creerCleExpedition } from "./expedition";
import type {
  CommandeDetail,
  CommandePreteExpedition,
  ExpeditionDetail,
} from "@/types/domain";
import { useAuth } from "@/auth/AuthProvider";

const erreurMessage = (erreur: unknown) =>
  erreur instanceof Error ? erreur.message : "Opération d'expédition impossible.";

export function ExpeditionPanel() {
  const { hasAnyPermission } = useAuth();
  const peutExpedier = hasAnyPermission("shipments.manage");
  const [commandes, setCommandes] = useState<CommandePreteExpedition[]>([]);
  const [commande, setCommande] = useState<CommandeDetail | null>(null);
  const [expedition, setExpedition] = useState<ExpeditionDetail | null>(null);
  const [historique, setHistorique] = useState<ExpeditionDetail[]>([]);
  const [quantites, setQuantites] = useState<Record<string, number>>({});
  const [preuve, setPreuve] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [confirmationKey, setConfirmationKey] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chargerFile = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCommandes(await expeditionsService.commandesPretes()); }
    catch (erreur) { setError(erreurMessage(erreur)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void chargerFile(); }, [chargerFile]);

  const ouvrirCommande = async (item: CommandePreteExpedition) => {
    setBusy(true); setError(null); setExpedition(null); setConfirmationKey(null);
    try {
      const [detail, historiqueCharge] = await Promise.all([
        commandesService.detail(item.commande_id),
        expeditionsService.historique(item.commande_id),
      ]);
      setCommande(detail); setHistorique(historiqueCharge);
      setQuantites(Object.fromEntries(detail.lignes.map((ligne) => [
        ligne.id, ligne.quantite_demandee - ligne.quantite_expediee,
      ])));
      if (item.brouillon_id) {
        setExpedition(await expeditionsService.detail(item.brouillon_id));
      }
    } catch (erreur) { setError(erreurMessage(erreur)); }
    finally { setBusy(false); }
  };

  const creerBrouillon = async () => {
    if (!commande) return;
    setBusy(true); setError(null);
    try {
      const payload = construireCreationExpedition(
        commande, quantites, creerCleExpedition("CREATE", commande.id),
      );
      const resultat = await expeditionsService.creer(commande.id, payload);
      setExpedition(resultat);
      setHistorique(await expeditionsService.historique(commande.id));
    } catch (erreur) { setError(erreurMessage(erreur)); }
    finally { setBusy(false); }
  };

  const demanderConfirmation = () => {
    if (!expedition) return;
    setConfirmationKey((cle) => cle || creerCleExpedition("CONFIRM", expedition.id));
    setConfirmationOpen(true);
  };

  const confirmer = async () => {
    if (!expedition || !confirmationKey) return;
    setBusy(true); setError(null); setConfirmationOpen(false);
    try {
      const resultat = await expeditionsService.confirmer(expedition.id, {
        idempotency_key: confirmationKey,
        preuve_depart: preuve.trim() || null,
        commentaire: commentaire.trim() || null,
      });
      setExpedition(resultat);
      setHistorique(await expeditionsService.historique(resultat.commande_id));
      await chargerFile();
    } catch (erreur) {
      // La clé est conservée : un retry réseau rejoue le même événement.
      setError(erreurMessage(erreur));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="expedition-panel">
      {loading ? <LoadingState label="Chargement des commandes PRET..." rows={4} /> : error && !commande ? (
        <ErrorState title="Expédition indisponible" description={error} onRetry={() => void chargerFile()} />
      ) : <section className="ops-panel p-4">
        <div className="mb-4 flex items-center gap-2"><Truck className="h-5 w-5" /><h2 className="font-semibold">Commandes prêtes à expédier</h2></div>
        {commandes.length === 0 ? <EmptyState title="Aucune commande PRET" description="Les commandes apparaissent après confirmation complète de la préparation." /> : (
          <div className="grid gap-3 lg:grid-cols-2">{commandes.map((item) => (
            <button key={item.commande_id} type="button" disabled={busy} onClick={() => void ouvrirCommande(item)} className="ops-focus rounded-sm border border-border p-4 text-left hover:bg-muted">
              <div className="flex justify-between gap-2"><strong>{item.numero_commande}</strong><StatusBadge status="PRET" /></div>
              <p className="mt-1 text-sm">{item.client} · PO {item.numero_po_client}</p>
              <p className="mt-2 font-mono text-xs">{item.quantite_restante} article(s) · {item.nombre_lignes_restantes} ligne(s)</p>
              {item.brouillon_id && <p className="mt-1 text-xs font-semibold text-amber-700">Brouillon à confirmer</p>}
            </button>
          ))}</div>
        )}
      </section>}

      {commande && <section className="ops-panel space-y-5 p-5" data-testid="expedition-workspace">
        <div className="flex justify-between gap-3"><div><p className="ops-label">Commande</p><h2 className="text-xl font-semibold">{commande.numero_commande}</h2><p className="text-sm text-muted-foreground">PO {commande.numero_po_client}</p></div><StatusBadge status={expedition?.commande.statut || commande.statut} /></div>

        {!expedition ? <>
          <div className="space-y-3">{commande.lignes.filter((ligne) => ligne.quantite_expediee < ligne.quantite_demandee).map((ligne) => {
            const restante = ligne.quantite_demandee - ligne.quantite_expediee;
            return <div key={ligne.id} className="grid items-center gap-3 rounded-sm border border-border p-4 md:grid-cols-[1fr_auto_auto]">
              <div><p className="font-mono font-semibold">{ligne.sku}</p><p>{ligne.description}</p><p className="text-xs text-muted-foreground">Déjà expédiée : {ligne.quantite_expediee} · Reliquat : {restante}</p></div>
              <label className="ops-label" htmlFor={`ship-${ligne.id}`}>À expédier</label>
              <input id={`ship-${ligne.id}`} type="number" min="0" max={restante} step="1" value={quantites[ligne.id] ?? 0} onChange={(event) => setQuantites((actuelles) => ({ ...actuelles, [ligne.id]: Number(event.target.value) }))} className="ops-focus h-10 w-24 rounded-sm border bg-background px-3 text-right font-mono" />
            </div>;
          })}</div>
          <p className="text-sm text-muted-foreground">Créer le brouillon ne modifie aucun stock. La déduction aura lieu seulement après confirmation explicite.</p>
          {peutExpedier && <button type="button" disabled={busy} onClick={() => void creerBrouillon()} className="ops-focus rounded-sm bg-primary px-5 py-3 font-semibold text-primary-foreground">Préparer l'expédition</button>}
        </> : <>
          <div className="rounded-sm border border-blue-300 bg-blue-50 p-4 dark:bg-blue-500/10">
            <div className="flex justify-between"><strong>Expédition {expedition.id.slice(0, 8)}</strong><StatusBadge status={expedition.statut} /></div>
            <div className="mt-3 space-y-2">{expedition.lignes.map((ligne) => <div key={ligne.id} className="flex justify-between border-t border-blue-200 pt-2 text-sm"><span><span className="font-mono">{ligne.sku}</span> — {ligne.description}</span><strong>{ligne.quantite_prevue}</strong></div>)}</div>
          </div>
          {expedition.statut === "BROUILLON" && <>
            <div className="grid gap-3 md:grid-cols-2"><div><label className="ops-label" htmlFor="preuve-depart">Preuve de départ (optionnelle)</label><input id="preuve-depart" value={preuve} onChange={(e) => setPreuve(e.target.value)} className="ops-focus mt-1 h-10 w-full rounded-sm border bg-background px-3" /></div><div><label className="ops-label" htmlFor="commentaire-expedition">Commentaire (optionnel)</label><input id="commentaire-expedition" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className="ops-focus mt-1 h-10 w-full rounded-sm border bg-background px-3" /></div></div>
            {peutExpedier && <button type="button" disabled={busy} onClick={demanderConfirmation} className="ops-focus rounded-sm bg-emerald-700 px-5 py-3 font-semibold text-white">Confirmer l'expédition réelle</button>}
          </>}
          {expedition.statut === "CONFIRMEE" && <div className="flex items-center gap-2 rounded-sm border border-emerald-300 bg-emerald-50 p-4 text-emerald-900 dark:bg-emerald-500/10"><PackageCheck className="h-5 w-5" /><strong>Expédition confirmée — {expedition.quantite_expediee} article(s)</strong></div>}
        </>}

        {error && <div role="alert" className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}{expedition?.statut === "BROUILLON" && confirmationKey && <button type="button" onClick={demanderConfirmation} className="ml-3 rounded-sm border px-3 py-1 font-semibold">Réessayer la même confirmation</button>}</div>}

        <div><div className="mb-3 flex items-center gap-2"><History className="h-4 w-4" /><h3 className="font-semibold">Historique</h3></div>{historique.length === 0 ? <p className="text-sm text-muted-foreground">Aucune expédition antérieure.</p> : <div className="space-y-2">{historique.map((item) => <div key={item.id} className="flex flex-wrap justify-between gap-2 rounded-sm border border-border p-3 text-sm"><span className="font-mono">{item.id.slice(0, 8)}</span><StatusBadge status={item.statut} /><span>{item.quantite_expediee} / {item.quantite_prevue} article(s)</span></div>)}</div>}</div>
      </section>}

      <ConfirmDialog open={confirmationOpen} onOpenChange={setConfirmationOpen} title="Confirmer l'expédition réelle?" description={`Cette action déduira ${expedition?.quantite_prevue || 0} article(s) du stock physique et réservé. Elle ne peut pas être annulée dans cette phase.`} cancelLabel="Retour" confirmLabel="Confirmer l'expédition" destructive={false} onConfirm={() => void confirmer()} testId="confirm-expedition" />
    </div>
  );
}
